import React, { useState } from 'react';
import {
  FaFireAlt, FaChartLine, FaShieldAlt, FaCheckCircle,
  FaChevronDown, FaCalculator, FaCoins, FaUmbrellaBeach, FaRunning
} from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';
const FirePlanning = () => {
  // State for Accordion FAQ toggling
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqs = [
    {
      q: "What is FIRE (Financial Independence, Retire Early)?",
      a: "FIRE is a lifestyle movement and financial strategy focused on extreme savings (often 50% to 70% of income) and disciplined investing during your peak earning years. The ultimate objective is to accumulate a corpus large enough so that passive investment returns cover your living expenses permanently, allowing you to stop working decades ahead of standard retirement age."
    },
    {
      q: "What is the '4% Rule' in FIRE planning?",
      a: "The 4% Rule is a guideline often used to determine how much money you can withdraw from your retirement portfolio each year without running out of money. It suggests that if you withdraw 4% of your total initial corpus in the first year of retirement—and adjust that amount for inflation annually—your portfolio has a high statistical probability of lasting for 30 years or more."
    },
    {
      q: "What is the difference between Lean FIRE, Fat FIRE, and Barista FIRE?",
      a: "Lean FIRE involves retiring early by living a minimalist lifestyle on reduced expenses. Fat FIRE means maintaining a comfortable or high-end lifestyle in retirement without budgeting restrictions. Barista FIRE refers to quitting your high-stress career to work a lower-stress, part-time job ('barista job') that covers day-to-day expenses while your core investments continue to compound."
    },
    {
      q: "How can Lotoswealth help me achieve FIRE?",
      a: "We help you calculate your precise FIRE number, structure an aggressive equity-heavy asset allocation strategy, optimize tax liabilities on capital gains, and map out a sustainable withdrawal roadmap tailored to your specific lifestyle goals."
    }
  ];

  return (
    <main className="fire-planning-page">
      <Helmet>
        {/* Basic SEO */}
        <title>FIRE Planning Services in Hyderabad | Financial Independence & Early Retirement | Lotoswealth</title>

        <meta
          name="description"
          content="Lotoswealth offers expert FIRE Planning Services in Hyderabad, helping individuals achieve Financial Independence and Retire Early through personalized investment strategies, wealth management, retirement planning, tax-efficient investing, and long-term financial planning."
        />

        <meta
          name="keywords"
          content="FIRE Planning Hyderabad, FIRE Planning Services Hyderabad, Financial Independence Hyderabad, Retire Early Hyderabad, Financial Independence Retire Early Hyderabad, Early Retirement Planning Hyderabad, Wealth Management Hyderabad, Financial Planning Hyderabad, Retirement Planning Hyderabad, Investment Planning Hyderabad, Goal Based Investing Hyderabad, Investment Advisor Hyderabad, Financial Advisor Hyderabad, Wealth Advisor Hyderabad, Asset Management Hyderabad, Mutual Funds Hyderabad, SIP Investment Hyderabad, Stocks Investment Hyderabad, Tax Planning Hyderabad, Long Term Wealth Creation Hyderabad, Lotoswealth"
        />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="Lotoswealth" />
        <meta name="language" content="English" />

        <link
          rel="canonical"
          href="https://www.lotoswealth.com/fire-planning"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Lotoswealth" />
        <meta
          property="og:url"
          content="https://www.lotoswealth.com/fire-planning"
        />
        <meta
          property="og:title"
          content="FIRE Planning Services in Hyderabad | Lotoswealth"
        />
        <meta
          property="og:description"
          content="Achieve Financial Independence and Retire Early with Lotoswealth's FIRE Planning Services in Hyderabad. Personalized investment planning, wealth management, tax optimization, and retirement strategies."
        />
        <meta
          property="og:image"
          content="https://www.lotoswealth.com/images/lotoswealth-logo.png"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="FIRE Planning Services in Hyderabad | Lotoswealth"
        />
        <meta
          name="twitter:description"
          content="Build wealth faster with FIRE Planning Services in Hyderabad. Expert guidance on Financial Independence, Early Retirement, investment planning, wealth management, and tax-efficient strategies."
        />
        <meta
          name="twitter:image"
          content="https://www.lotoswealth.com/images/lotoswealth-logo.png"
        />
      </Helmet>
      {/* =========================================
          1. HERO SECTION (With Ticker & Slow Zoom)
      ========================================= */}
      <div className="hero-wrapper position-relative" style={{ height: '100vh', minHeight: '800px', overflow: 'hidden' }}>

        <div className="position-absolute top-0 start-0 w-100 h-100 z-0 animate-slow-zoom">
          <img
            src="/images/financial-independence-retire-early-planning.jpg"
            alt="FIRE Planning Services in Hyderabad and India"
            className="w-100 h-100 object-fit-cover"
          />
        </div>

        {/* Slanted Panels Overlay */}
        <div className="panels-container position-absolute top-0 start-0 w-100 h-100 z-1 d-flex">
          <div className="slanted-panel panel-1"></div>
          <div className="slanted-panel panel-2"></div>
          <div className="slanted-panel panel-3"></div>
          <div className="slanted-panel panel-4"></div>
        </div>

        {/* --- Floating Main Header Text (Perfectly Centered) --- */}
        <div className="container hero-main-text text-center position-absolute top-50 start-50 translate-middle z-3 w-100 px-4">
          <h1 className="fw-bold display-4 animate-slide-up delay-1 mb-3 text-white" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>
            FIRE Planning <br />
          </h1> <p>  <span className="text-warning"> ( Financial Independence Retire Early Planning ) </span> </p>
          <h2 className="animate-slide-up delay-2 fw-bold text-white mb-4" style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            Reclaim Your Time and Achieve Ultimate Financial Freedom
          </h2>
          <p className="hero-description animate-slide-up delay-3 text-white mx-auto" style={{ maxWidth: '950px', fontSize: '1.1rem', lineHeight: '1.7', textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            Welcome to Lotoswealth. For many professionals, the traditional 30-year corporate grind is no longer the only option. Our specialized FIRE Planning advisory is designed for high-savers and ambitious wealth creators who want to accelerate their journey to financial independence and break free from the 9-to-5 decades ahead of schedule.
          </p>
          <p className="hero-description mt-3 animate-slide-up delay-4 text-white mx-auto" style={{ maxWidth: '950px', fontSize: '1.1rem', lineHeight: '1.7', textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            Achieving FIRE requires more than just saving money—it demands mathematical precision, aggressive equity compounding, optimized expense tracking, and a bulletproof withdrawal strategy. We help you calculate your exact FIRE number and map out a disciplined roadmap to get there.
          </p>
          <p className="hero-description mt-3 animate-slide-up delay-5 text-white mx-auto" style={{ maxWidth: '950px', fontSize: '1.1rem', lineHeight: '1.7', textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            Design a life where work becomes a choice, not a necessity. Take control of your timeline with expert FIRE guidance.
          </p>
        </div>

        {/* --- Scrolling Stock Ticker --- */}
        <div className="stock-ticker position-absolute bottom-0 w-100 z-3 bg-dark text-white border-top border-bottom border-secondary py-2" style={{ opacity: 0.9 }}>
          <div className="ticker-track">
            {/* First Set */}
            <div className="ticker-item text-white border-end border-secondary"><strong>APOLLOHOSP</strong> 8,894.00 <span className="text-danger">-63.00 (-0.70%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>ASIANPAINT</strong> 2,768.60 <span className="text-success">21.30 (0.78%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>AXISBANK</strong> 1,250.00 <span className="text-success">20.50 (1.67%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>BAJAJ-AUTO</strong> 11,519.00 <span className="text-danger">-2.00 (-0.02%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>BAJAJFINSV</strong> 2,085.60 <span className="text-success">56.50 (2.78%)</span></div>

            {/* Duplicated Set for Infinite Scrolling */}
            <div className="ticker-item text-white border-end border-secondary"><strong>APOLLOHOSP</strong> 8,894.00 <span className="text-danger">-63.00 (-0.70%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>ASIANPAINT</strong> 2,768.60 <span className="text-success">21.30 (0.78%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>AXISBANK</strong> 1,250.00 <span className="text-success">20.50 (1.67%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>BAJAJ-AUTO</strong> 11,519.00 <span className="text-danger">-2.00 (-0.02%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>BAJAJFINSV</strong> 2,085.60 <span className="text-success">56.50 (2.78%)</span></div>
          </div>
        </div>
      </div>

      {/* =========================================
          2. WHY FIRE PLANNING MATTERS (Soft Teal Theme)
      ========================================= */}
      <section className="py-5" style={{ backgroundColor: '#f0f9f6' }}>
        <div className="container py-4">
          <div className="row align-items-center g-5">
            <div className="col-lg-6 animate-slide-up delay-1">
              <h2 className="fw-bold mb-4 text-dark">
                The Mathematics & Philosophy of FIRE
              </h2>
              <p className="text-muted line-height-lg">
                The core philosophy of FIRE is simple: your savings rate is far more important than your income. By cultivating mindful spending habits and maximizing your investment rate, you drastically shorten the timeline required to achieve total financial freedom.
              </p>
              <p className="text-muted line-height-lg">
                Traditional retirement assumes you stop working at 60. FIRE redefines retirement as the point where your investments generate enough passive income to cover 100% of your annual expenses—whether that happens at age 40, 45, or 50.
              </p>
            </div>
            <div className="col-lg-6 animate-slide-up delay-2">
              <div className="p-4 bg-white rounded-4 shadow-sm border-start border-teal border-5">
                <h4 className="fw-bold text-teal mb-3">Core Pillars of FIRE</h4>
                <ul className="list-unstyled text-muted mb-0">
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>High Savings Rate:</strong> Retaining 50%+ of earnings to accelerate compounding.</li>
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>The FIRE Number:</strong> Calculating your exact target corpus (25x annual expenses).</li>
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Equity Acceleration:</strong> Harnessing growth-oriented equity portfolios.</li>
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Safe Withdrawal:</strong> Managing post-retirement drawdowns securely.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================
          3. OUR FIRE SERVICES (Crisp White)
      ========================================= */}
      <section className="py-5 bg-white" style={{ backgroundImage: "url('/images/lotoswealth-bg.jpg')", backgroundSize: 'cover', backgroundPosition: 'center' }}>

        <div className="container py-4">
          <div className="text-center mb-5 animate-slide-up delay-1">
            <h2 className="fw-bold text-white">🔥 Our FIRE Planning Solutions</h2>
            <p className="text-white">Structured strategies to help you reach financial independence faster.</p>
          </div>

          <div className="row g-4">

            <div className="col-lg-4 col-md-6 animate-slide-up delay-1">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaCalculator size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">FIRE Number & Milestone Modeling</h5>
                <p className="text-muted small">We analyze your current lifestyle costs, inflation expectations, and target retirement age to compute your exact FIRE target corpus.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-2">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaChartLine size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Aggressive Growth Portfolio Design</h5>
                <p className="text-muted small">Build equity-heavy, high-compounding asset allocations designed to maximize wealth accumulation during your peak earning years.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-3">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaRunning size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Lean, Fat & Barista FIRE Strategy</h5>
                <p className="text-muted small">Explore which FIRE sub-category fits your lifestyle ambitions—whether it's minimalist living, uncompromised luxury, or part-time career transitions.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-4">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaCoins size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Safe Withdrawal & SWP Structuring</h5>
                <p className="text-muted small">Design tax-efficient post-FIRE cash flow withdrawal systems (like the 4% rule and bucket strategies) to fund decades of early retirement safely.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-5">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaShieldAlt size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Early Retirement Healthcare Planning</h5>
                <p className="text-muted small">Structure independent health insurance buffers and contingency funds since early retirees cannot rely on corporate group medical covers.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-6">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaUmbrellaBeach size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Tax Optimization on Capital Gains</h5>
                <p className="text-muted small">Minimize tax leakages on early portfolio liquidations through strategic long-term capital gains planning and tax harvesting.</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* =========================================
          4. OUR 5-STEP FIRE PROCESS (Dark Slate)
      ========================================= */}
      <section className="py-5 text-white" style={{ backgroundColor: '#0a192f' }}>
        <div className="container py-4">
          <div className="text-center mb-5 animate-slide-up delay-1">
            <h2 className="fw-bold text-white">🔄 Our FIRE Planning Methodology</h2>
            <p className="text-light opacity-75">Your step-by-step roadmap to escaping the rat race.</p>
          </div>

          <div className="row g-4">

            <div className="col-md-6 col-lg-4 animate-slide-up delay-1">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 1 – Expense Audit & Savings Rate</h5>
                <p className="small text-muted mb-0">We track your annual expenditure to establish your baseline living cost and optimize your current savings rate.</p>
              </div>
            </div>

            <div className="col-md-6 col-lg-4 animate-slide-up delay-2">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 2 – FIRE Number Calculation</h5>
                <p className="small text-muted mb-0">Multiplying your annual expenses by 25 (or your chosen multiplier) to define your exact financial freedom milestone.</p>
              </div>
            </div>

            <div className="col-md-6 col-lg-4 animate-slide-up delay-3">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 3 – Acceleration Strategy</h5>
                <p className="small text-muted mb-0">Deploying high-compounding equity portfolios and optimizing asset allocation to maximize velocity towards your goal.</p>
              </div>
            </div>

            <div className="col-md-6 col-lg-6 animate-slide-up delay-4">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 4 – Bridge Planning</h5>
                <p className="small text-muted mb-0">Structuring bridge accounts for early retirees to access funds before statutory retirement age limits without penalty.</p>
              </div>
            </div>

            <div className="col-md-12 col-lg-6 animate-slide-up delay-5">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 5 – Execution & Milestone Review</h5>
                <p className="small text-muted mb-0">Continuous tracking of your net worth milestones until your passive income officially eclipses your living expenses.</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* =========================================
          5. WHO WE SERVE & WHY CHOOSE US (Soft Blue-Gray)
      ========================================= */}
      <section className="py-5" style={{ backgroundColor: '#eef2f5' }}>
        <div className="container py-4">
          <div className="row g-5">

            {/* Target Audience */}
            <div className="col-lg-5 animate-slide-up delay-1">
              <h3 className="fw-bold mb-4 text-dark">👥 Who Is FIRE Planning For?</h3>
              <p className="text-muted small mb-4">The FIRE movement is ideal for driven individuals seeking time freedom:</p>
              <div className="d-flex flex-wrap gap-2">
                {[
                  "High-Earning Tech Professionals", "Aggressive Young Savers",
                  "Entrepreneurs Seeking Early Exits", "Minimalist Lifestyle Enthusiasts",
                  "Corporate Executives Wanting Freedom", "Dual-Income No-Kids (DINK) Households",
                  "Forward-Thinking Wealth Builders", "Time-Freedom Seekers"
                ].map((item, idx) => (
                  <span key={idx} className="badge bg-white text-dark border p-2 shadow-sm fs-6">
                    {item}
                  </span>
                ))}
              </div>
            </div>

            {/* Why Choose Lotoswealth */}
            <div className="col-lg-7 animate-slide-up delay-2">
              <div className="p-4 bg-white rounded-4 shadow-sm h-100">
                <h3 className="fw-bold mb-3 text-dark">🏆 Why Lotoswealth for FIRE?</h3>
                <p className="text-muted small mb-4">FIRE requires custom mathematical modeling and aggressive growth strategies. Clients choose us because we offer:</p>
                <div className="row g-3">
                  {[
                    "Precise FIRE Number Computation", "Aggressive Equity-Heavy Compounding Strategies",
                    "Independent Healthcare Buffer Planning", "Tax-Efficient Early Withdrawal Models",
                    "Unbiased Asset & Fund Selection", "Transparent Guidance & Clear Roadmaps",
                    "Dedicated Wealth Advisors", "Long-Term Financial Independence Focus"
                  ].map((item, idx) => (
                    <div key={idx} className="col-md-6 d-flex align-items-center">
                      <FaCheckCircle className="text-success me-2 flex-shrink-0" />
                      <span className="small text-muted fw-bold">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* =========================================
          6. FREQUENTLY ASKED QUESTIONS (FAQ)
      ========================================= */}
      <section className="py-5" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container py-4" style={{ maxWidth: '850px' }}>
          <div className="text-center mb-5 animate-slide-up delay-1">
            <h2 className="fw-bold text-dark">❓ Frequently Asked Questions</h2>
            <p className="text-muted">Common queries regarding FIRE Planning & Early Retirement</p>
          </div>

          <div className="d-flex flex-column gap-3 animate-slide-up delay-2">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-3 shadow-sm border overflow-hidden"
              >
                <button
                  className="w-100 text-start p-3 border-0 bg-white d-flex justify-content-between align-items-center fw-bold text-dark"
                  onClick={() => toggleFaq(index)}
                  style={{ cursor: 'pointer' }}
                >
                  <span className="pe-3">{faq.q}</span>
                  <FaChevronDown
                    className={`text-teal transition-transform ${openFaq === index ? 'rotate-180' : ''}`}
                    style={{ transition: 'transform 0.3s ease', minWidth: '16px' }}
                  />
                </button>
                {openFaq === index && (
                  <div className="p-3 pt-0 text-muted small border-top bg-light lh-lg">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================
          7. FINAL CTA SECTION (Deep Navy)
      ========================================= */}
      <section className="py-5 text-white text-center" style={{ backgroundColor: '#0f1e2d' }}>
        <div className="container py-4">
          <h2 className="fw-bold mb-3 animate-slide-up delay-1">Ready to Accelerate Your Journey to Financial Independence?</h2>
          <p className="text-light opacity-75 mb-4 animate-slide-up delay-2 mx-auto" style={{ maxWidth: '600px' }}>
            Calculate your FIRE number and build an aggressive accumulation strategy today. Take control of your time and your future.
          </p>
          <button className="btn-custom-teal hover-lift px-5 py-3 fs-5 border-0 rounded text-white shadow animate-slide-up delay-3">
            Schedule a FIRE Planning Consultation
          </button>
        </div>
      </section>

    </main>
  );
};

export default FirePlanning;